import { action, makeObservable, observable, runInAction } from 'mobx';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import Routes from '../../routes';
import { Team } from '../../entities/team';
import { hasVisibleGuidedTrainingLibrary } from '../../components/guidedTraining/guidedTrainingCore';

type RedirectType = null | 'url' | 'team' | 'userDashboard' | 'agencyDashboard' | 'onboardTeam' | 'createTeam';

interface RouteMatch {
  path: string;
  pathname: string;
  params: {
    teamId: string;
  };
}

export class DefaultPageState
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this, {
      isLoaded: observable,
      lastVisitedLibrary: observable,
      redirectTo: observable,
      firstTeam: observable,
      pageMounted: action,
      pageUnmounted: action,
    });
  }

  isLoaded: boolean = false;
  lastVisitedLibrary: RouteMatch | null = null;
  redirectTo: RedirectType = null;
  firstTeam: Team | null = null;

  async pageMounted() {
    this.lastVisitedLibrary = this.store.storage.getLocal(
      'router.lastVisitedLibrary',
      true
    );
    const showOnboarding = this.store.storage.getLocal(
      'user.showOnboarding',
    );

    await this.store.teamManager.apiEnsureTeams();
    this.firstTeam = this.store.teamManager.teams.find(team => !team.hideUserDetails) || null;
    const shouldRunCreateFirstLibraryTraining = !!showOnboarding
      && !!this.store.user?.hasPermission('CreateTeam')
      && !hasVisibleGuidedTrainingLibrary(this.store.teamManager.teams);

    if (this.store.user?.isAgencySponsor) {
      this.redirectTo = 'agencyDashboard';
    } else if (this.lastVisitedLibrary) {
      const { params, path } = this.lastVisitedLibrary;

      if (shouldRunCreateFirstLibraryTraining) {
        this.redirectTo = 'userDashboard';
      } else if (showOnboarding) {
        this.redirectTo = 'onboardTeam';
      } else if (this.shouldRedirectToLastVisitedLibrary(path, params.teamId)) {
        this.redirectTo = 'url';
      } else {
        this.store.storage.removeLocal('router.lastVisitedLibrary');
      }
    } else {
      if (shouldRunCreateFirstLibraryTraining) {
        this.redirectTo = 'userDashboard';
      } else if (showOnboarding) {
        this.redirectTo = 'onboardTeam';
      } else if (this.firstTeam && this.firstTeam.id) {
        this.redirectTo = 'team';
      } else if (this.store.user?.hasPermission('CreateTeam')) {
        this.redirectTo = 'createTeam';
      } else if (this.store.user?.hasPermission('ViewUserDashboard')) {
        this.redirectTo = 'userDashboard';
      }
    }

    runInAction(() => {
      this.isLoaded = true;
    });
  }

  pageUnmounted() {
    this.firstTeam = null;
    this.redirectTo = null;
    this.isLoaded = false;
  }

  shouldRedirectToLastVisitedLibrary(
    path: string,
    teamId: string
  ): boolean {
    if (
      teamId &&
      path === Routes.teamDashboard() &&
      this.store.teamManager.hasTeam(teamId) &&
      !this.store.teamManager.getTeam(teamId)?.hideUserDetails &&
      this.store.teamManager.getTeam(teamId)?.hasPermission('ViewTeam')
    ) {
      return true;
    } else if (
      path === Routes.trainerDashboard() &&
      this.store.user?.hasPermission('ViewTrainerDashboard')
    ) {
      return true;
    } else if (
      path === Routes.userDashboard() &&
      this.store.user?.hasPermission('ViewUserDashboard')
    ) {
      return true;
    } else if (
      path === Routes.adminTeamsDashboard() &&
      this.store.user?.hasPermission('AdminGetTeams')
    ) {
      return true;
    }
    return false;
  }
}
